import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { Item } from '@openx/components-targeting/Targeting/shared/SelectedItems/Item.tsx';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { exportType } from '../../../../../constants.ts';
import type { Group, GroupFieldName } from '../../../../../types.ts';
import { AudienceFields, type AudienceForm } from '../../../../../types.ts';
import { ConfirmationModal } from '../../../../ConfirmationModal.tsx';
import { useMarketConfirmationModal } from '../../../hooks/useMarketConfirmationModal.ts';
import { useMarketCountries } from '../../../hooks/useMarketCountries.ts';
import { useMarketDisplay } from '../../../hooks/useMarketDisplay.ts';
import { useMarketRegion } from '../../../hooks/useMarketRegion.ts';
import { useMarketSection } from '../../../hooks/useMarketSection.ts';
import { BaseFilterSection } from '../BaseFilterSection.tsx';
import { ReadOnlyMarketSelection } from './ReadOnlyMarketSelection.tsx';

export type MarketSectionProps = {
  groupFieldName: GroupFieldName;
  update: (index: number, value: Group) => void;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onCollapseCriteriaSections: () => void;
  isReadOnly?: boolean;
};

export const MarketSection = ({
  groupFieldName,
  update,
  index,
  isExpanded,
  onToggleExpand,
  onCollapseCriteriaSections,
  isReadOnly = false,
}: MarketSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { control } = useFormContext<AudienceForm>();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control }) ?? '';
  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });
  const observableProviderId = useWatch({ name: AudienceFields.DirectAudienceProvider, control }) ?? undefined;
  const isOaMatchType = observableExportType === exportType.OA_MATCH;

  const {
    selectedRegionCode,
    setSelectedRegion,
    selectedCountries,
    setSelectedCountries,
    isRegionSelected,
    clearRegion,
    hasSegments,
    changeRegionAndClearSegments,
  } = useMarketSection({
    groupFieldName,
    update,
    index,
  });

  const { activeModal, pendingRegionCode, openClearModal, handleRegionChangeWithModal, closeModal, confirmModal } =
    useMarketConfirmationModal({
      hasSegments,
      setSelectedRegion,
      changeRegionAndClearSegments,
      clearRegion,
      onToggleExpand,
      onCollapseCriteriaSections,
    });

  const { regions, regionsLoading, handleRegionChange } = useMarketRegion({
    onRegionChange: handleRegionChangeWithModal,
    accountId: observableAccountId,
    isOaMatchType,
    providerId: observableProviderId,
    exportType: observableExportType,
  });

  const regionNameByCode = useMemo(() => new Map(regions.map(region => [region.code, region.name])), [regions]);
  const regionCodes = useMemo(() => Array.from(regionNameByCode.keys()), [regionNameByCode]);

  const { countriesLoading, countries, handleAddCountry, handleRemoveCountry, getCountryName } = useMarketCountries({
    selectedRegionCode,
    selectedCountries,
    setSelectedCountries,
    accountId: observableAccountId,
    isOaMatchType,
    providerId: observableProviderId,
    exportType: observableExportType,
  });

  const {
    countryInputValue,
    setCountryInputValue,
    availableCountries,
    shouldFetchCountries,
    selectedCount,
    confirmationItem,
    pendingRegionLabel,
  } = useMarketDisplay({
    selectedRegionCode,
    isRegionSelected,
    selectedCountries,
    pendingRegionCode,
    regions,
    countries,
  });

  const isCountriesDropdownDisabled = countriesLoading || countries.length === 0;

  return (
    <>
      <BaseFilterSection
        titleKey="Market"
        dataTestId="market-section"
        selectedCount={selectedCount}
        onClear={openClearModal}
        showClearButton={!isReadOnly && selectedCount > 0}
        hideCountInTitle
        hideSubtitle
        expandableWhenEmpty
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
      >
        <Box sx={{ px: 2 }}>
          {!isReadOnly && (
            <AutoComplete
              textFieldProps={{
                label: t('Region'),
                margin: 'dense',
                placeholder: t('Choose a region'),
              }}
              options={regionCodes}
              getOptionLabel={option => regionNameByCode.get(option) ?? option}
              value={selectedRegionCode ?? undefined}
              onChange={handleRegionChange}
              disableClearable
              loading={regionsLoading}
              disabled={regionsLoading}
              data-test="market-section-select"
            />
          )}

          {isReadOnly && selectedRegionCode && (
            <ReadOnlyMarketSelection
              variant="region"
              loading={regionsLoading}
              items={[regionNameByCode.get(selectedRegionCode) ?? selectedRegionCode]}
            />
          )}

          {shouldFetchCountries && !isReadOnly && (
            <IndentedSections dataTest="market-countries-indent">
              <AutoCompleteVirtualize
                options={availableCountries}
                inputValue={countryInputValue}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setCountryInputValue(value);
                  }
                }}
                onChange={(_, newValue) => {
                  handleAddCountry(newValue.code);
                  setCountryInputValue('');
                }}
                onClose={() => {
                  setCountryInputValue('');
                }}
                getOptionLabel={option => option.name}
                loading={countriesLoading}
                disabled={isCountriesDropdownDisabled}
                textFieldProps={{
                  placeholder: t('[ Select or Search for Country ]'),
                  label: t('Country'),
                  margin: 'dense',
                  sx: { mt: 0 },
                }}
                disableClearable
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderOptions={{
                  getOptionValue: option => option.name,
                  childrenLocation: 'above',
                  renderChildren: option =>
                    selectedCountries.includes(option.code) ? (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}>
                        <CheckCircleIcon
                          data-test="market-country-option-check-icon"
                          color={theme.palette.success.main}
                          size={20}
                        />
                      </Box>
                    ) : null,
                }}
                fullWidth
                data-test="market-section-countries-select"
              />

              {selectedCountries.length > 0 && (
                <Grid container sx={{ mt: 0.5 }} data-test="market-included-list">
                  <Grid sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon
                      data-test="market-included-check-icon"
                      color={theme.palette.success.main}
                      size={20}
                    />
                  </Grid>
                  {selectedCountries.map((code, idx) => (
                    <Grid key={code}>
                      <Item
                        testId="market-country-item"
                        readonly={isReadOnly}
                        item={code}
                        onRemove={isReadOnly ? undefined : handleRemoveCountry}
                      >
                        {getCountryName(code)}
                        {idx < selectedCountries.length - 1 ? ',' : ''}
                      </Item>
                    </Grid>
                  ))}
                </Grid>
              )}
            </IndentedSections>
          )}

          {shouldFetchCountries && isReadOnly && selectedCountries.length > 0 && (
            <ReadOnlyMarketSelection
              variant="countries"
              loading={countriesLoading}
              items={selectedCountries.map(code => getCountryName(code))}
            />
          )}
        </Box>
      </BaseFilterSection>

      <ConfirmationModal
        isOpen={activeModal !== null}
        onClose={closeModal}
        onDelete={confirmModal}
        title={activeModal === 'clear' ? t('Clear region selection') : t('Change Audience Region')}
        item={activeModal === 'clear' ? confirmationItem : (pendingRegionLabel ?? t('Selected region'))}
        bodyText={
          activeModal === 'clear'
            ? t('You are about to clear the following selection:')
            : t('Switching to a new market region will remove all audience segments currently applied to this group.')
        }
        confirmLabel={activeModal === 'clear' ? t('Clear') : t('Change Region')}
      />
    </>
  );
};
